import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { auth, GoogleAuthProvider, FacebookAuthProvider } from '../Firebase';
import { isFacebookLoginOn, facebookErrorMessage, facebookLinkEmail, linkHeldFacebook } from '../utils/facebookLogin';
import FacebookLinkNotice from '../components/FacebookLinkNotice';
import { signInWith, takeRedirectSignIn, hasPendingRedirect } from '../utils/socialSignIn';
import SigningInOverlay from '../components/SigningInOverlay';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import './Login.css';
import { useInAppGoogleGuard } from '../components/OpenInBrowserPrompt';

const CreateAccount = () => {
  const { guard, prompt: openInBrowserPrompt } = useInAppGoogleGuard();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  // Facebook found an existing account with this email (null = no).
  const [fbLinkEmail, setFbLinkEmail] = useState(null);
  // Back from a Google / Facebook redirect: "Signing you in…", not this form.
  const [finishing, setFinishing] = useState(() => hasPendingRedirect('create-account'));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('back') || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Please enter your name.'); return; }
    if (!termsAccepted) { setError('Please accept the terms and conditions to continue.'); return; }
    setLoading(true);
    setError('');
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(cred.user, { displayName: name });
      navigate(backUrl);
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError(<span>This email is already registered. <Link to={`/login?back=${encodeURIComponent(backUrl)}`} className="signupform-error-link">Sign in here</Link>.</span>);
      } else if (err.code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else {
        setError('Sign up failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const googleError = (err) => {
    if (err.code !== 'auth/popup-closed-by-user') setError('Google sign-in failed. Please try again.');
  };

  const facebookError = (err) => {
    const linkEmail = facebookLinkEmail(err);
    if (linkEmail !== null) { setFbLinkEmail(linkEmail); return; }
    const message = facebookErrorMessage(err);
    if (message) setError(message);
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const result = await signInWith(new GoogleAuthProvider(), { page: 'create-account' });
      if (!result) return; // redirecting
      await linkHeldFacebook(result.user); // after "already have an account" on Facebook
      navigate(backUrl);
    } catch (err) {
      googleError(err);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleFacebook = async () => {
    setFacebookLoading(true);
    setError('');
    try {
      const provider = new FacebookAuthProvider();
      provider.addScope('email');
      const result = await signInWith(provider, { page: 'create-account' });
      if (result) navigate(backUrl);
    } catch (err) {
      facebookError(err);
    } finally {
      setFacebookLoading(false);
    }
  };

  // Back from a Google / Facebook redirect (phones, in-app browsers).
  useEffect(() => {
    let cancelled = false;
    takeRedirectSignIn('create-account').then(async (back) => {
      if (cancelled) return;
      if (!back) { setFinishing(false); return; }
      const onError = back.pending.provider === 'facebook' ? facebookError : googleError;
      if (back.error) { setFinishing(false); onError(back.error); return; }
      if (back.pending.provider === 'google') await linkHeldFacebook(back.result.user);
      navigate(backUrl);
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (finishing) return <SigningInOverlay />;

  return (
    <div className="login-page-bg">
      <form className="form-container login-form" onSubmit={handleSubmit}>
        <h1 className="form-heading">Create your account</h1>
        <p style={{ fontSize: '0.9rem', color: '#64748b', margin: '0 0 1rem', textAlign: 'center', lineHeight: 1.5 }}>
          Free to join — no community required. You can explore and join communities anytime.
        </p>

        <label className="form-label" htmlFor="ca-name">Your name</label>
        <input className="form-input" id="ca-name" type="text" value={name} onChange={e => setName(e.target.value)} required placeholder="Your full name" />

        <label className="form-label" htmlFor="ca-email">Email</label>
        <input className="form-input" id="ca-email" type="email" autoComplete="username" value={email} onChange={e => setEmail(e.target.value)} required />

        <label className="form-label" htmlFor="ca-password">Password</label>
        <input className="form-input" id="ca-password" type="password" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} placeholder="At least 6 characters" />

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.85rem', color: '#374151', cursor: 'pointer', lineHeight: 1.5 }}>
          <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} style={{ marginTop: '2px', flexShrink: 0, accentColor: '#6366f1' }} />
          I agree to the <Link to="/terms" target="_blank" style={{ color: '#6366f1' }}>Terms and Conditions</Link> and <Link to="/privacy" target="_blank" style={{ color: '#6366f1' }}>Privacy Policy</Link>
        </label>

        <button className="button w-full" type="submit" disabled={loading || !termsAccepted}>
          {loading ? 'Creating account…' : 'Create account'}
        </button>

        <div className="login-links">
          <Link to={`/login?back=${encodeURIComponent(backUrl)}`} className="forgot-link">Already have an account? Sign in</Link>
        </div>

        {error && <div className="form-error">{error}</div>}

        <div className="social-buttons">
          <button className="social-btn social-btn-google" type="button" onClick={guard(handleGoogle)} disabled={googleLoading}>
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.618 14.013 17.64 11.706 17.64 9.2z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
            </svg>
            {googleLoading ? 'Signing in…' : 'Continue with Google'}
          </button>
          {openInBrowserPrompt}
          {/* Hidden until the Meta app is Live — see utils/facebookLogin.js. */}
          {isFacebookLoginOn() && (
          <button className="social-btn social-btn-facebook" type="button" onClick={handleFacebook} disabled={facebookLoading}>
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
              <path fill="#fff" d="M18 9a9 9 0 1 0-10.406 8.89v-6.29H5.309V9h2.285V7.017c0-2.256 1.344-3.503 3.4-3.503.985 0 2.015.176 2.015.176v2.215h-1.135c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.4 2.6H10.41v6.29A9.002 9.002 0 0 0 18 9z"/>
            </svg>
            {facebookLoading ? 'Signing in…' : 'Continue with Facebook'}
          </button>
          )}
          {fbLinkEmail !== null && <FacebookLinkNotice email={fbLinkEmail} onGoogle={guard(handleGoogle)} onClose={() => setFbLinkEmail(null)} />}
        </div>
      </form>
    </div>
  );
};

export default CreateAccount;
